'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Accessibility,
  Activity,
  Bath,
  Bed,
  Bone,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Cigarette,
  Cross,
  Droplet,
  Ear,
  Flower2,
  Footprints,
  Gift,
  Grid,
  Hand,
  HeartPulse,
  Luggage,
  List,
  ListFilter,
  Monitor,
  Moon,
  Package,
  PackageOpen,
  PawPrint,
  PersonStanding,
  Search,
  Scale,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  SprayCan,
  TestTube,
  VenetianMask,
  Wind,
  X,
  Shirt,
  Pill,
  Heart,
  ArrowRight,
  Stethoscope,
  Baby,
  Dumbbell,
  Thermometer,
  Eye,
  Smile,
  Leaf,
  Sun,
  Brain,
  Apple,
  Droplets,
  Syringe,
  Tablets,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';
import { ProductCard } from '@/components/ProductCard';
import { ProductCardSkeleton, Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { useCategories, useFeaturedProducts } from '@/hooks/useProducts';
import { ProductFilters } from '@/lib/types';
import { formatPrice } from '@/lib/utils/format';
import { filterProductsByPriceRange, findDefaultVariantForPriceRange } from '@/lib/utils/productFilters';
import Image from 'next/image';
import React from 'react';
import { useSearchParams } from 'next/navigation';
import { usePersonalizedProducts } from '@/hooks/usePersonalizedProducts';
import { PersonalizedProductsQueryDtoSortEnum } from '@/lib/Apis';

type SortOption = 'featured' | 'price-low-high' | 'price-high-low' | 'newest';
type ProductView = 'grid' | 'list';

const PRICE_SLIDER_MAX = 200;

interface ShopScreenProps {
  initialFilters?: ProductFilters;
  categoryName?: string;
}

const getPageFromSearchParams = (searchParams: Pick<URLSearchParams, 'get'>) => {
  const requestedPage = Number(searchParams.get('page'));
  return Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
};

const normalizeMultiSelect = (multiple?: string[], single?: string) =>
  multiple?.length ? multiple : single ? [single] : [];

// Rules are intentionally ordered from specific product groups to broad concepts.
// That keeps names such as "Digestive Health" from being caught by the generic
// health/care rules before a more useful icon is found.
const CATEGORY_ICON_RULES = [
  { keywords: ['compression stocking', 'compression sock', 'hosiery', 'varicose'], icon: Footprints },
  { keywords: ['durable medical equipment', 'dme', 'wheelchair', 'walker', 'rollator', 'crutch', 'mobility aid', 'walking cane'], icon: Accessibility },
  { keywords: ['first aid', 'wound', 'bandage', 'gauze', 'antiseptic', 'burn care'], icon: Cross },
  { keywords: ['disposable medical', 'medical supplies', 'surgical supplies', 'exam supplies'], icon: PackageOpen },
  { keywords: ['ppe', 'face mask', 'surgical mask', 'protective equipment'], icon: VenetianMask },
  { keywords: ['otc', 'over the counter', 'nonprescription', 'prescription', 'medicine', 'medication', 'pharmaceutical', 'pharmacy', 'drug'], icon: Tablets },
  { keywords: ['vitamin', 'supplement', 'mineral', 'probiotic', 'multivitamin'], icon: Pill },
  { keywords: ['cough', 'cold and flu', 'cold flu', 'fever', 'temperature', 'thermometer'], icon: Thermometer },
  { keywords: ['allergy', 'sinus', 'nasal', 'congestion'], icon: Wind },
  { keywords: ['respiratory', 'asthma', 'inhaler', 'nebulizer', 'oxygen', 'breathing'], icon: Wind },
  { keywords: ['heart', 'cardiac', 'cardiovascular', 'blood pressure', 'hypertension'], icon: HeartPulse },
  { keywords: ['diabetes', 'diabetic', 'glucose', 'insulin', 'blood sugar'], icon: Droplet },
  { keywords: ['diagnostic', 'laboratory', 'lab test', 'test kit', 'testing'], icon: TestTube },
  { keywords: ['medical device', 'monitoring', 'monitor', 'health device'], icon: Monitor },
  { keywords: ['baby', 'infant', 'toddler', 'child', 'pediatric', 'nursing'], icon: Baby },
  { keywords: ['maternity', 'pregnancy', 'pregnant', 'prenatal', 'postpartum'], icon: Baby },
  { keywords: ['feminine', 'women', 'menstrual', 'period care'], icon: Flower2 },
  { keywords: ['mens health', 'men care', 'male health'], icon: PersonStanding },
  { keywords: ['personal care', 'personal hygiene', 'body care', 'deodorant'], icon: Heart },
  { keywords: ['bath', 'shower', 'bathing'], icon: Bath },
  { keywords: ['incontinence', 'bladder', 'urology', 'urinary'], icon: Droplets },
  { keywords: ['dental', 'oral', 'tooth', 'teeth', 'mouth care'], icon: Smile },
  { keywords: ['eye', 'vision', 'optical', 'contact lens'], icon: Eye },
  { keywords: ['ear', 'hearing', 'audiology'], icon: Ear },
  { keywords: ['skin', 'dermatology', 'acne', 'sunscreen', 'sun care'], icon: Sun },
  { keywords: ['hair', 'grooming', 'shaving'], icon: SprayCan },
  { keywords: ['beauty', 'cosmetic', 'makeup'], icon: Sparkles },
  { keywords: ['foot care', 'podiatry', 'heel care'], icon: Footprints },
  { keywords: ['bone', 'orthopedic', 'joint', 'arthritis', 'brace support'], icon: Bone },
  { keywords: ['pain relief', 'muscle pain', 'recovery', 'rehabilitation', 'therapy'], icon: Activity },
  { keywords: ['fitness', 'sport', 'exercise', 'gym'], icon: Dumbbell },
  { keywords: ['weight management', 'weight loss', 'slimming'], icon: Scale },
  { keywords: ['nutrition', 'diet', 'healthy food', 'meal replacement'], icon: Apple },
  { keywords: ['digestive', 'digestion', 'stomach', 'gut health', 'constipation', 'diarrhea'], icon: Apple },
  { keywords: ['hydration', 'electrolyte', 'water', 'fluid replacement'], icon: Droplets },
  { keywords: ['immune', 'immunity', 'infection', 'covid'], icon: ShieldCheck },
  { keywords: ['vaccine', 'vaccination', 'injection', 'syringe'], icon: Syringe },
  { keywords: ['natural', 'organic', 'herbal', 'homeopathic', 'botanical'], icon: Leaf },
  { keywords: ['mental', 'brain', 'cognitive', 'memory', 'focus'], icon: Brain },
  { keywords: ['sleep', 'insomnia', 'bedtime'], icon: Moon },
  { keywords: ['lifestyle', 'wellness', 'self care'], icon: Sparkles },
  { keywords: ['sexual health', 'intimate care', 'contraceptive', 'family planning'], icon: Heart },
  { keywords: ['smoking', 'tobacco', 'nicotine', 'smoking cessation'], icon: Cigarette },
  { keywords: ['travel', 'traveler'], icon: Luggage },
  { keywords: ['home care', 'bed care', 'bedroom aids'], icon: Bed },
  { keywords: ['scrub', 'uniform', 'medical apparel', 'workwear'], icon: Shirt },
  { keywords: ['pet', 'veterinary', 'animal care'], icon: PawPrint },
  { keywords: ['gift', 'gift set'], icon: Gift },
  { keywords: ['aromatherapy', 'essential oil'], icon: Flower2 },
  { keywords: ['hand care', 'hand sanitizer'], icon: Hand },
  { keywords: ['clinical', 'clinic', 'doctor', 'general health', 'health care'], icon: Stethoscope },
  { keywords: ['equipment', 'supplies'], icon: PackageOpen },
  { keywords: ['care'], icon: Heart },
] as const;

const getCategoryIcon = (categoryName: string) => {
  const normalizedName = categoryName
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  return CATEGORY_ICON_RULES.find(({ keywords }) =>
    keywords.some((keyword) => normalizedName.includes(keyword))
  )?.icon ?? Package;
};

export function ShopScreen({ initialFilters = {}, categoryName }: ShopScreenProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const searchParam = searchParams.get('q') || '';
  const { buildPath } = useBasePath();

  // State
  const [filters, setFilters] = useState<ProductFilters>(initialFilters);
  const page = getPageFromSearchParams(searchParams);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('featured');
  const [productView, setProductView] = useState<ProductView>('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [priceSliderValues, setPriceSliderValues] = useState({ min: 0, max: PRICE_SLIDER_MAX });
  const [knownBrands, setKnownBrands] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedFilterSections, setExpandedFilterSections] = useState<Record<string, boolean>>({
    category: true,
    brand: false,
    availability: true,
    price: true,
  });
  const [mobileExpandedFilterSections, setMobileExpandedFilterSections] = useState<Record<string, boolean>>({
    category: true,
    brand: false,
    availability: true,
    price: false,
  });
  const [isMobile, setIsMobile] = useState(false);
  const productsStartRef = useRef<HTMLDivElement>(null);
  const categoryRailRef = useRef<HTMLDivElement>(null);
  const previousPageRef = useRef(page);
  const [categoryScrollState, setCategoryScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const setPage = useCallback((nextPage: number) => {
    const normalizedPage = Math.max(1, Math.floor(nextPage));
    const params = new URLSearchParams(window.location.search);
    const currentPage = getPageFromSearchParams(params);

    if (currentPage === normalizedPage) return;

    if (normalizedPage === 1) {
      params.delete('page');
    } else {
      params.set('page', String(normalizedPage));
    }

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    router.push(nextUrl, { scroll: false });
  }, [router]);

  // Track screen size for responsive placeholder
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    setIsMobile(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!showMobileFilters) return;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowMobileFilters(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMobileFilters]);

  // Keep the filters and page heading visible, but move the first product row into view.
  useEffect(() => {
    if (previousPageRef.current === page) return;

    previousPageRef.current = page;
    const animationFrame = window.requestAnimationFrame(() => {
      productsStartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [page]);

  const backendSort = useMemo((): PersonalizedProductsQueryDtoSortEnum | undefined => {
    switch (sortOption) {
      case 'price-low-high': return PersonalizedProductsQueryDtoSortEnum.PriceAsc;
      case 'price-high-low': return PersonalizedProductsQueryDtoSortEnum.PriceDesc;
      case 'newest': return PersonalizedProductsQueryDtoSortEnum.Latest;
      default: return undefined;
    }
  }, [sortOption]);

  // Query - using personalized products hook instead of generic useProducts
  const { products, isLoading, pagination, isPersonalized, personalizedCount, availableBrands: catalogBrands } = usePersonalizedProducts({
    filters,
    page,
    limit: 20,
    personalizedRatio: sortOption === 'featured' ? 40 : 0, // Only personalize when featured sort is active
    featuredFirst: sortOption === 'featured',
    sort: backendSort,
  });
  const { categories, isLoading: isLoadingCategories } = useCategories();
  const { featuredProduct } = useFeaturedProducts();
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const sanitized = searchQuery.trim().replace(/\s+/g, ' ');
    if (sanitized) {
      setIsSearching(true);
      router.push(buildPath(`/search?q=${encodeURIComponent(sanitized)}`));
    }
  };

  // Handle search input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle search when pressing Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const sanitized = searchQuery.trim().replace(/\s+/g, ' ');
      if (sanitized) {
        setIsSearching(true);
        router.push(buildPath(`/search?q=${encodeURIComponent(sanitized)}`));
      }
    }
  };

  useEffect(() => {
    setPriceSliderValues({
      min: Math.max(0, Math.min(filters.minPrice ?? 0, PRICE_SLIDER_MAX - 1)),
      max: Math.max(1, Math.min(filters.maxPrice ?? PRICE_SLIDER_MAX, PRICE_SLIDER_MAX)),
    });
  }, [filters.minPrice, filters.maxPrice]);

  // Real-time filtering for search sidebar REMOVED to fix "basic search" issue
  // Search is now only triggered on Enter or Search Button click


  // Auto-expand active category or parent of active subcategory
  useEffect(() => {
    const updates: Record<string, boolean> = {};
    normalizeMultiSelect(filters.categories, filters.category).forEach((categoryId) => {
      updates[categoryId] = true;
    });
    if (filters.subCategory) {
      const parent = categories.find((c) =>
        c.categorySubCategories?.some((sc) => (sc._id ?? sc.id) === filters.subCategory)
      );
      const parentId = parent?._id ?? parent?.id;
      if (parentId) updates[parentId] = true;
    }
    if (Object.keys(updates).length) {
      setExpandedCategories((prev) => ({ ...prev, ...updates }));
    }
  }, [filters.category, filters.categories, filters.subCategory, categories]);

  const toggleCategoryExpand = useCallback((id: string) => {
    setExpandedCategories((prev) => {
      const isExpanded = !!prev[id];
      // If closing, return empty (or remove id). If opening, return ONLY this id.
      return isExpanded ? {} : { [id]: true };
    });
  }, []);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0),
    [categories]
  );
  const updateCategoryScrollState = useCallback(() => {
    const rail = categoryRailRef.current;
    if (!rail) return;

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
    setCategoryScrollState({
      canScrollLeft: rail.scrollLeft > 2,
      canScrollRight: rail.scrollLeft < maxScrollLeft - 2,
    });
  }, []);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(updateCategoryScrollState);
    window.addEventListener('resize', updateCategoryScrollState);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', updateCategoryScrollState);
    };
  }, [categories, isLoadingCategories, updateCategoryScrollState]);

  const scrollCategories = useCallback((direction: -1 | 1) => {
    const rail = categoryRailRef.current;
    if (!rail) return;

    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.72, 280),
      behavior: 'smooth',
    });
  }, []);


  const filteredProducts = useMemo(() => {
    if (isLoading) return products;
    let items = [...products];

    // Client-side inStock filter - ensures products with zero inventory are filtered out
    if (filters.inStock) {
      items = items.filter((p) => {
        const hasStockViaVariants = p.variants?.some((v: any) => (v.inventoryCount ?? 0) > 0);
        const hasStockViaSummary = (p.summary?.totalInventory ?? 0) > 0;
        return hasStockViaVariants || hasStockViaSummary;
      });
    }

    // Search filter
    if (filters.search) {
      const query = filters.search.toLowerCase().trim();
      items = items.filter((p) =>
        p.name?.toLowerCase().includes(query) ||
        p.brand?.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query) ||
        (Array.isArray(p.tags) && p.tags.some((t: string) => t.toLowerCase().includes(query))) ||
        (Array.isArray(p.variants) && p.variants.some((v: any) =>
          v.name?.toLowerCase().includes(query) ||
          v.sku?.toLowerCase().includes(query) ||
          v.description?.toLowerCase().includes(query) ||
          (v.attributes && typeof v.attributes === 'object' && Object.values(v.attributes).some(val =>
            typeof val === 'string' && val.toLowerCase().includes(query)
          ))
        ))
      );
    }

    if (filters.tags?.length) {
      items = items.filter((p) => Array.isArray(p.tags) && p.tags.some((t: string) => filters.tags!.includes(t)));
    }
    if (filters.newArrivals) {
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      items = items.filter((p) => new Date(p.createdAt).getTime() >= thirtyDaysAgo);
    }
    const selectedBrands = normalizeMultiSelect(filters.brands, filters.brand);
    if (selectedBrands.length) {
      const normalizedBrands = new Set(selectedBrands.map((brand) => brand.trim().toLowerCase()));
      items = items.filter((p) => p.brand && normalizedBrands.has(p.brand.trim().toLowerCase()));
    }

    const min = filters.minPrice ?? 0;
    const max = filters.maxPrice ?? Infinity;
    const hasPriceFilter = filters.minPrice !== undefined || filters.maxPrice !== undefined;
    items.forEach((p) => {
      if (!p.variants || p.variants.length === 0) {
        const price = (p as any).finalPrice ?? (p as any).price ?? 0;
      } else {
        (p.variants as any[]).forEach((v: any) => {
          const price = v.finalPrice ?? v.price ?? 0;
          const inRange = hasPriceFilter && price >= min && price <= max;
        });
      }
    });

    // Client-side price filter — include product if ANY variant falls within [minPrice, maxPrice].
    if (hasPriceFilter) {
      items = filterProductsByPriceRange(items, filters.minPrice, filters.maxPrice);
    }

    return items;
  }, [isLoading, products, filters.search, filters.inStock, filters.tags, filters.newArrivals, filters.brand, filters.brands, filters.minPrice, filters.maxPrice]);

  const displayedProducts = filteredProducts;

  // Keep discovered brands available while filters change. The API supplies the
  // complete list; accumulating product brands also preserves compatibility with
  // older API deployments during rollout.
  useEffect(() => {
    const brandSet = new Set<string>(knownBrands);
    catalogBrands.forEach((brand) => brandSet.add(brand));
    products.forEach((product) => {
      if (product.brand && product.brand.trim()) {
        brandSet.add(product.brand.trim());
      }
    });
    normalizeMultiSelect(filters.brands, filters.brand).forEach((brand) => brandSet.add(brand));
    const nextBrands = Array.from(brandSet).sort();
    if (nextBrands.length !== knownBrands.length || nextBrands.some((brand, index) => brand !== knownBrands[index])) {
      setKnownBrands(nextBrands);
    }
  }, [catalogBrands, filters.brand, filters.brands, knownBrands, products]);

  const availableBrands = knownBrands;

  const handleCategoryChange = useCallback(
    (categoryId: string) => {
      setFilters((current) => {
        const selected = normalizeMultiSelect(current.categories, current.category);
        const isSelected = selected.includes(categoryId);
        const nextSelected = isSelected
          ? selected.filter((id) => id !== categoryId)
          : [...selected, categoryId];
        const next = { ...current } as ProductFilters;

        delete next.category;
        if (nextSelected.length) next.categories = nextSelected;
        else delete next.categories;

        if (isSelected && next.subCategory) {
          const removedCategory = categories.find((category) => (category._id ?? category.id) === categoryId);
          const ownsActiveSubcategory = removedCategory?.categorySubCategories?.some(
            (subCategory) => (subCategory._id ?? subCategory.id) === next.subCategory
          );
          if (ownsActiveSubcategory) delete next.subCategory;
        }

        return next;
      });
      setPage(1);
    },
    [categories]
  );

  const handleSubCategoryChange = useCallback((parentCategoryId: string, subCategoryId: string) => {
    setFilters((current) => {
      if (current.subCategory === subCategoryId) {
        const { subCategory, ...rest } = current as any;
        return rest as ProductFilters;
      }
      const selected = normalizeMultiSelect(current.categories, current.category);
      const next = { ...current, subCategory: subCategoryId } as ProductFilters;
      delete next.category;
      next.categories = selected.includes(parentCategoryId) ? selected : [...selected, parentCategoryId];
      return next;
    });
    setPage(1);
  }, []);

  const handleToggleStock = useCallback(() => {
    setFilters((current) => {
      if (current.inStock) {
        const { inStock, ...rest } = current;
        return rest;
      }
      return { ...current, inStock: true };
    });
    setPage(1);
  }, []);

  const handleToggleSale = useCallback(() => {
    setFilters((current) => {
      if (current.onSale) {
        const { onSale, ...rest } = current;
        return rest;
      }
      return { ...current, onSale: true };
    });
    setPage(1);
  }, []);

  const handleToggleNewArrivals = useCallback(() => {
    setFilters((current) => {
      if (current.newArrivals) {
        const { newArrivals, ...rest } = current;
        return rest as typeof current;
      }
      return { ...current, newArrivals: true };
    });
    setPage(1);
  }, []);

  const handleClearCategory = useCallback(() => {
    setFilters((current) => {
      const { category, categories, subCategory, ...rest } = current as any;
      return rest;
    });
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setSearchQuery('');
    setPriceSliderValues({ min: 0, max: PRICE_SLIDER_MAX });
    setPage(1);
  }, []);

  const handleRemoveCategory = useCallback((categoryId: string) => {
    setFilters((current) => {
      const selected = normalizeMultiSelect(current.categories, current.category);
      const nextSelected = selected.filter((id) => id !== categoryId);
      const next = { ...current } as ProductFilters;
      delete next.category;
      if (nextSelected.length) next.categories = nextSelected;
      else delete next.categories;

      if (next.subCategory) {
        const removedCategory = categories.find((category) => (category._id ?? category.id) === categoryId);
        if (removedCategory?.categorySubCategories?.some((sub) => (sub._id ?? sub.id) === next.subCategory)) {
          delete next.subCategory;
        }
      }

      return next;
    });
    setPage(1);
  }, [categories]);

  const handleRemoveSubCategory = useCallback(() => {
    setFilters((current) => {
      const next = { ...current } as any;
      delete next.subCategory;
      return next as ProductFilters;
    });
    setPage(1);
  }, []);

  const handleRemoveSearch = useCallback(() => {
    setFilters((current) => {
      const { search, ...rest } = current;
      return rest;
    });
    setSearchQuery('');
    setPage(1);
  }, []);

  const handleRemoveInStock = useCallback(() => {
    setFilters((current) => {
      const { inStock, ...rest } = current;
      return rest;
    });
    setPage(1);
  }, []);

  const handleRemoveSale = useCallback(() => {
    setFilters((current) => {
      const { onSale, ...rest } = current;
      return rest;
    });
    setPage(1);
  }, []);

  const handleRemovePrice = useCallback(() => {
    setFilters((current) => {
      const next = { ...current };
      delete next.minPrice;
      delete next.maxPrice;
      return next;
    });
    setPriceSliderValues({ min: 0, max: PRICE_SLIDER_MAX });
    setPage(1);
  }, []);

  const handleRemoveTag = useCallback((tag: string) => {
    setFilters((current) => {
      if (!current.tags) return current;
      const updated = current.tags.filter((item) => item !== tag);
      const next = { ...current };
      if (updated.length) {
        next.tags = updated;
      } else {
        delete next.tags;
      }
      return next;
    });
    setPage(1);
  }, []);

  const handleBrandChange = useCallback((brand: string) => {
    setFilters((current) => {
      const selected = normalizeMultiSelect(current.brands, current.brand);
      const nextSelected = selected.includes(brand)
        ? selected.filter((item) => item !== brand)
        : [...selected, brand];
      const next = { ...current } as ProductFilters;
      delete next.brand;
      if (nextSelected.length) next.brands = nextSelected;
      else delete next.brands;
      return next;
    });
    setPage(1);
  }, []);

  const handleRemoveBrand = useCallback((brand: string) => {
    setFilters((current) => {
      const selected = normalizeMultiSelect(current.brands, current.brand);
      const nextSelected = selected.filter((item) => item !== brand);
      const next = { ...current } as ProductFilters;
      delete next.brand;
      if (nextSelected.length) next.brands = nextSelected;
      else delete next.brands;
      return next;
    });
    setPage(1);
  }, []);

  const handlePriceSliderChange = useCallback((handle: 'min' | 'max', rawValue: number) => {
    setPriceSliderValues((current) => {
      const next = handle === 'min'
        ? { ...current, min: Math.min(rawValue, current.max - 1) }
        : { ...current, max: Math.max(rawValue, current.min + 1) };

      setFilters((currentFilters) => {
        const nextFilters = { ...currentFilters };

        if (next.min > 0) nextFilters.minPrice = next.min;
        else delete nextFilters.minPrice;

        if (next.max < PRICE_SLIDER_MAX) nextFilters.maxPrice = next.max;
        else delete nextFilters.maxPrice;

        return nextFilters;
      });

      return next;
    });
    setPage(1);
  }, []);

  const {
    search: searchFilter,
    category: categoryFilter,
    categories: categoryFilters,
    subCategory: subCategoryFilter,
    inStock: inStockOnly,
    onSale: onSaleOnly,
    minPrice,
    maxPrice,
    tags,
    newArrivals,
    brand: brandFilter,
    brands: brandFilters,
  } = filters;

  const selectedCategoryIds = useMemo(
    () => normalizeMultiSelect(categoryFilters, categoryFilter),
    [categoryFilter, categoryFilters]
  );
  const selectedBrands = useMemo(
    () => normalizeMultiSelect(brandFilters, brandFilter),
    [brandFilter, brandFilters]
  );

  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];

    if (searchFilter) {
      chips.push({
        key: 'search',
        label: `Search: "${searchFilter}"`,
        onRemove: handleRemoveSearch,
      });
    }

    if (subCategoryFilter) {
      let subName: string | undefined;
      categories.forEach((cat) => {
        const found = cat.categorySubCategories?.find((sc) => (sc._id ?? sc.id) === subCategoryFilter);
        if (found) {
          subName = found.name;
        }
      });
      chips.push({
        key: 'subcategory',
        label: `Subcategory: ${subName ?? subCategoryFilter}`,
        onRemove: handleRemoveSubCategory,
      });
    }

    selectedCategoryIds.forEach((categoryId) => {
      const category = categories.find((cat) => (cat._id ?? cat.id) === categoryId);
      chips.push({
        key: `category-${categoryId}`,
        label: `Category: ${category?.name ?? categoryId}`,
        onRemove: () => handleRemoveCategory(categoryId),
      });
    });

    if (inStockOnly) {
      chips.push({
        key: 'stock',
        label: 'In stock',
        onRemove: handleRemoveInStock,
      });
    }

    if (onSaleOnly) {
      chips.push({
        key: 'sale',
        label: 'On sale',
        onRemove: handleRemoveSale,
      });
    }

    if (minPrice !== undefined || maxPrice !== undefined) {
      const minLabel = minPrice !== undefined ? formatPrice(minPrice) : 'Any';
      const maxLabel = maxPrice !== undefined ? formatPrice(maxPrice) : 'Any';
      chips.push({
        key: 'price',
        label: `Price: ${minLabel} - ${maxLabel}`,
        onRemove: handleRemovePrice,
      });
    }

    if (tags?.length) {
      tags.forEach((tag) => {
        chips.push({
          key: `tag-${tag}`,
          label: `Tag: ${tag}`,
          onRemove: () => handleRemoveTag(tag),
        });
      });
    }

    if (newArrivals) {
      chips.push({
        key: 'new-arrivals',
        label: 'New arrivals',
        onRemove: handleToggleNewArrivals,
      });
    }

    selectedBrands.forEach((brand) => {
      chips.push({
        key: `brand-${brand}`,
        label: `Brand: ${brand}`,
        onRemove: () => handleRemoveBrand(brand),
      });
    });

    return chips;
  }, [
    categories,
    selectedCategoryIds,
    subCategoryFilter,
    handleRemoveCategory,
    handleRemoveSubCategory,
    handleRemoveInStock,
    handleRemoveSale,
    handleRemovePrice,
    handleRemoveSearch,
    handleRemoveTag,
    handleRemoveBrand,
    inStockOnly,
    onSaleOnly,
    maxPrice,
    minPrice,
    searchFilter,
    tags,
    newArrivals,
    selectedBrands,
  ]);

  const hasActiveFilters = activeFilterChips.length > 0;

  const toggleFilterSection = useCallback((section: string) => {
    setExpandedFilterSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const toggleMobileFilterSection = useCallback((section: string) => {
    setMobileExpandedFilterSections((previous) => ({
      ...previous,
      [section]: !previous[section],
    }));
  }, []);

  const renderFiltersPanel = (mobile = false) => {
    const expandedSections = mobile ? mobileExpandedFilterSections : expandedFilterSections;
    const toggleSection = mobile ? toggleMobileFilterSection : toggleFilterSection;

    return (
    <div className={mobile ? 'w-full' : 'lg:w-72'}>
      <div className={mobile ? 'overflow-hidden bg-white' : 'overflow-hidden rounded-3xl border border-slate-100 bg-white'}>
        <section className="border-b border-slate-100 px-6 pb-6 pt-7">
          <h3 className="font-serif text-[22px] italic text-hsecondary">Filters</h3>
          {mobile && (
            <p className="mt-1 text-[15px] font-medium text-[#9AA9A1]">Adjust the settings, then apply</p>
          )}
          <label
            htmlFor="sidebar-product-search"
            className="mt-4 block text-[13px] font-medium text-[#9AA9A1]"
          >
            Search Products
          </label>
          <form onSubmit={handleSearch} className="relative mt-3">
            <Search
              className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#9AA9A1]"
              strokeWidth={1.8}
            />
            <input
              id="sidebar-product-search"
              type="search"
              value={searchQuery}
              onChange={handleInputChange}
              placeholder="Search..."
              disabled={isSearching}
              className="h-11 w-full rounded-[14px] border border-slate-200 bg-white pl-10 pr-4 text-[14px] text-hsecondary outline-none transition placeholder:text-slate-300 focus:border-hsecondary/40 focus:ring-2 focus:ring-hsecondary/10 disabled:cursor-wait disabled:opacity-60"
            />
          </form>
        </section>

        <section className="border-b border-slate-100 px-6 py-5">
          <button
            type="button"
            onClick={() => toggleSection('category')}
            className="flex w-full items-center justify-between text-left"
            aria-expanded={expandedSections.category}
          >
            <span className="flex items-center gap-2 font-serif text-[19px] italic text-hsecondary">
              Category
              {selectedCategoryIds.length > 0 && (
                <span className="flex min-w-5 items-center justify-center rounded-full bg-hsecondary px-1.5 py-0.5 font-sans text-[10px] font-semibold not-italic text-white">
                  {selectedCategoryIds.length}
                </span>
              )}
            </span>
            <ChevronDown className={`size-4 text-slate-500 transition-transform ${expandedSections.category ? 'rotate-180' : ''}`} />
          </button>

          {expandedSections.category && (
            <div className="mt-4 space-y-3">
              {sortedCategories.map((category) => {
                const categoryId = category._id ?? category.id ?? '';
                const isCategoryActive = selectedCategoryIds.includes(categoryId);
                const isExpanded = !!expandedCategories[categoryId];
                const hasSubCategories = !!category.categorySubCategories?.length;

                return (
                  <div key={categoryId}>
                    <button
                      type="button"
                      onClick={() => {
                        if (hasSubCategories) toggleCategoryExpand(categoryId);
                        handleCategoryChange(categoryId);
                      }}
                      className="group flex w-full items-center gap-3 text-left"
                      aria-pressed={isCategoryActive}
                    >
                      <span className={`flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${isCategoryActive ? 'border-hsecondary bg-hsecondary text-white' : 'border-slate-300 bg-slate-50 group-hover:border-hsecondary/50'}`}>
                        {isCategoryActive && <Check className="size-3" strokeWidth={2.5} />}
                      </span>
                      <span className={`min-w-0 flex-1 text-[15px] leading-5 ${isCategoryActive ? 'font-semibold text-hsecondary' : 'font-medium text-[#7C9085]'}`}>
                        {category.name}
                      </span>
                      {hasSubCategories && (
                        <ChevronDown className={`size-3.5 shrink-0 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      )}
                    </button>

                    {hasSubCategories && isExpanded && (
                      <div className="ml-7 mt-3 space-y-2 border-l border-slate-100 pl-3">
                        {category.categorySubCategories?.map((sub) => {
                          const subId = sub._id ?? sub.id ?? '';
                          const isSubActive = subCategoryFilter === subId;
                          return (
                            <button
                              type="button"
                              key={subId}
                              onClick={() => handleSubCategoryChange(categoryId, subId)}
                              className={`block w-full text-left text-[13px] leading-5 transition-colors ${isSubActive ? 'font-semibold text-hsecondary' : 'text-[#7C9085] hover:text-hsecondary'}`}
                            >
                              {sub.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="border-b border-slate-100 px-6 py-5">
          <button
            type="button"
            onClick={() => toggleSection('brand')}
            className="flex w-full items-center justify-between text-left"
            aria-expanded={expandedSections.brand}
          >
            <span className="flex items-center gap-2 font-serif text-[19px] italic text-hsecondary">
              Brand
              {selectedBrands.length > 0 && (
                <span className="flex min-w-5 items-center justify-center rounded-full bg-hsecondary px-1.5 py-0.5 font-sans text-[10px] font-semibold not-italic text-white">
                  {selectedBrands.length}
                </span>
              )}
            </span>
            <ChevronDown className={`size-4 text-slate-500 transition-transform ${expandedSections.brand ? 'rotate-180' : ''}`} />
          </button>

          {expandedSections.brand && (
            <div className="mt-4 space-y-3">
              {availableBrands.length === 0 ? (
                <p className="text-sm text-[#7C9085]">No brands available</p>
              ) : availableBrands.map((brand) => {
                const isSelected = selectedBrands.includes(brand);
                return (
                  <button
                    type="button"
                    key={brand}
                    onClick={() => handleBrandChange(brand)}
                    className="group flex w-full items-center gap-3 text-left"
                    aria-pressed={isSelected}
                  >
                    <span className={`flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${isSelected ? 'border-hsecondary bg-hsecondary text-white' : 'border-slate-300 bg-slate-50 group-hover:border-hsecondary/50'}`}>
                      {isSelected && <Check className="size-3" strokeWidth={2.5} />}
                    </span>
                    <span className={`text-[15px] leading-5 ${isSelected ? 'font-semibold text-hsecondary' : 'font-medium text-[#7C9085]'}`}>
                      {brand}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Availability filter is temporarily hidden. Keep the implementation here
            so it can be restored without rebuilding its stock/sale behavior.
        <section className="border-b border-slate-100 px-6 py-5">
          <button
            type="button"
            onClick={() => toggleFilterSection('availability')}
            className="flex w-full items-center justify-between text-left"
            aria-expanded={expandedFilterSections.availability}
          >
            <span className="font-serif text-[19px] italic text-hsecondary">Availability</span>
            <ChevronDown className={`size-4 text-slate-500 transition-transform ${expandedFilterSections.availability ? 'rotate-180' : ''}`} />
          </button>

          {expandedFilterSections.availability && (
            <div className="mt-4 space-y-3">
              {[
                { label: 'In Stock', selected: !!inStockOnly, onClick: handleToggleStock },
                { label: 'On Sale', selected: !!onSaleOnly, onClick: handleToggleSale },
              ].map((option) => (
                <button
                  type="button"
                  key={option.label}
                  onClick={option.onClick}
                  className="group flex w-full items-center gap-3 text-left"
                  aria-pressed={option.selected}
                >
                  <span className={`flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${option.selected ? 'border-hsecondary bg-hsecondary text-white' : 'border-slate-300 bg-slate-50 group-hover:border-hsecondary/50'}`}>
                    {option.selected && <Check className="size-3" strokeWidth={2.5} />}
                  </span>
                  <span className={`text-[15px] leading-5 ${option.selected ? 'font-semibold text-hsecondary' : 'font-medium text-[#7C9085]'}`}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
        */}

        <section className="px-6 py-5">
          <button
            type="button"
            onClick={() => toggleSection('price')}
            className="flex w-full items-center justify-between text-left"
            aria-expanded={expandedSections.price}
          >
            <span className="font-serif text-[19px] italic text-hsecondary">Price Range</span>
            <ChevronDown className={`size-4 text-slate-500 transition-transform ${expandedSections.price ? 'rotate-180' : ''}`} />
          </button>

          {expandedSections.price && (
            <div className="mt-5">
              <div className="relative h-4">
                <div className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-200" />
                <div
                  className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-950"
                  style={{
                    left: `${(priceSliderValues.min / PRICE_SLIDER_MAX) * 100}%`,
                    right: `${100 - (priceSliderValues.max / PRICE_SLIDER_MAX) * 100}%`,
                  }}
                />
                <input
                  type="range"
                  min={0}
                  max={PRICE_SLIDER_MAX}
                  step={5}
                  value={priceSliderValues.min}
                  onChange={(event) => handlePriceSliderChange('min', Number(event.target.value))}
                  aria-label="Minimum price"
                  className="hp-price-range absolute inset-0 w-full"
                />
                <input
                  type="range"
                  min={0}
                  max={PRICE_SLIDER_MAX}
                  step={5}
                  value={priceSliderValues.max}
                  onChange={(event) => handlePriceSliderChange('max', Number(event.target.value))}
                  aria-label="Maximum price"
                  className="hp-price-range absolute inset-0 w-full"
                />
              </div>
              <div className="mt-4 flex items-center justify-between text-[13px] font-medium text-[#9AA9A1]">
                <span>${priceSliderValues.min}</span>
                <span>${priceSliderValues.max}{priceSliderValues.max === PRICE_SLIDER_MAX ? '+' : ''}</span>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
    );
  };

  const allProductsCount = useMemo(
    () => categories.reduce((sum, category) => sum + (category.productCount ?? 0), 0),
    [categories]
  );

  const formatItemCount = (count: number) => `${count} ${count === 1 ? 'Item' : 'Items'}`;

  return (
    <div className="min-h-screen ">
      {/* Header Section */}
      <section className="relative overflow-hidden bg-white pb-12 pt-8 md:pt-16">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-hprimary/5 rounded-[50%] blur-3xl -translate-y-1/2" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-hsecondary/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        </div>

        <div className="container mx-auto px-4 relative z-1">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto space-y-8 text-center"
          >
            {/* Badge */}
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-hprimary/20 bg-hprimary/5 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-hprimary shadow-xs">
                <Sparkles className="h-3.5 w-3.5" />
                Complete Pharmacy Shop
              </span>
            </div>

            {/* Main Title */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.1]">
                Medical Supplies & <br className="hidden md:block" />
                <span className="text-hsecondary/80 italic">
                  Wellness Products
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
                Expertly curated healthcare essentials. From professional medical supplies to daily vitamins, we have everything you need to stay healthy.
              </p>
            </div>

            {/* Search Bar */}
            <form
              onSubmit={handleSearch}
              className="relative mx-auto max-w-[672px]"
            >
              <div className="relative w-full">
                <div className="absolute left-6 top-1/2 flex -translate-y-1/2 items-center justify-center">
                  <Search className="size-5 text-[#9AA9A1]" strokeWidth={1.8} />
                </div>
                <input
                  type="search"
                  placeholder={isMobile ? "Search..." : "Search products or brands..."}
                  value={searchQuery}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="h-16 w-full rounded-full border border-slate-200 bg-white pl-14 pr-16 text-[17px] font-medium text-hsecondary shadow-[0_12px_28px_rgba(31,41,55,0.06)] outline-none transition placeholder:font-medium placeholder:text-[#ADB8B2] focus:border-hsecondary/40 focus:ring-2 focus:ring-hsecondary/10"
                  disabled={isSearching}
                />
                <AnimatePresence>
                  {searchQuery && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8, x: -10 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.8, x: -10 }}
                      type="submit"
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-hprimary p-2.5 text-white shadow-sm transition-colors hover:bg-hprimary/90"
                      title="Search"
                    >
                      <ArrowRight className="size-5" />
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Shop by Category Section */}
      <section className="bg-white pb-7 pt-11 md:pt-12">
        <div className="container mx-auto max-w-[1600px] px-4">
          <div className="mb-6 flex items-center justify-between md:mb-7">
            <h2 className="font-serif text-[26px] font-normal italic leading-tight text-hsecondary md:text-[29px]">
              Shop by Category
            </h2>
            <div className="hidden items-center gap-2 lg:flex" aria-label="Category carousel controls">
              <button
                type="button"
                onClick={() => scrollCategories(-1)}
                disabled={!categoryScrollState.canScrollLeft}
                aria-label="Scroll categories left"
                className="flex size-10 items-center justify-center rounded-full border border-slate-200 bg-white text-hsecondary transition-colors hover:border-hsecondary/30 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
              >
                <ChevronLeft className="size-5" strokeWidth={1.8} />
              </button>
              <button
                type="button"
                onClick={() => scrollCategories(1)}
                disabled={!categoryScrollState.canScrollRight}
                aria-label="Scroll categories right"
                className="flex size-10 items-center justify-center rounded-full border border-slate-200 bg-white text-hsecondary transition-colors hover:border-hsecondary/30 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
              >
                <ChevronRight className="size-5" strokeWidth={1.8} />
              </button>
            </div>
          </div>

          <div
            ref={categoryRailRef}
            onScroll={updateCategoryScrollState}
            className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {/* All Products */}
            <motion.button
              type="button"
              onClick={handleClearCategory}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35 }}
              aria-pressed={selectedCategoryIds.length === 0}
              className={`flex h-[104px] min-w-[104px] snap-start items-center justify-center rounded-[17px] border px-3 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hsecondary/30 lg:h-[70px] lg:min-w-[173px] lg:justify-start lg:gap-3 lg:text-left ${selectedCategoryIds.length === 0
                ? 'border-hsecondary bg-hsecondary text-white shadow-sm'
                : 'border-slate-200 bg-white text-hsecondary hover:border-hsecondary/25 hover:bg-slate-50'
                }`}
            >
              <span className={`hidden size-11 shrink-0 items-center justify-center rounded-[14px] lg:flex ${selectedCategoryIds.length === 0 ? 'bg-white/15' : 'bg-slate-100'}`}>
                <ShoppingBag className="size-5" strokeWidth={1.7} />
              </span>
              <div className="flex min-w-0 flex-col items-center lg:items-start">
                <span className="block text-[14px] font-semibold leading-[18px] lg:whitespace-nowrap lg:text-[15px] lg:leading-5">All Products</span>
                <span className={`block whitespace-nowrap text-[12px] leading-4 ${selectedCategoryIds.length === 0 ? 'text-white/65' : 'text-slate-400'}`}>
                  {formatItemCount(allProductsCount)}
                </span>
              </div>
            </motion.button>

            {isLoadingCategories && categories.length === 0 && Array.from({ length: 7 }).map((_, index) => (
              <div
                key={`category-skeleton-${index}`}
                className="h-[104px] w-[104px] shrink-0 animate-pulse rounded-[17px] border border-slate-200 bg-slate-50 lg:h-[70px] lg:w-[168px]"
              />
            ))}

            {categories.map((category, index) => {
              const Icon = getCategoryIcon(category.name ?? '');
              const categoryId = category._id ?? category.id ?? '';
              const isSelected = selectedCategoryIds.includes(categoryId);

              return (
                <motion.button
                  type="button"
                  key={categoryId}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.035, 0.25), duration: 0.35 }}
                  onClick={() => handleCategoryChange(categoryId)}
                  aria-pressed={isSelected}
                  className={`flex h-[104px] w-[104px] min-w-[104px] snap-start items-center justify-center rounded-[17px] border px-3 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hsecondary/30 lg:h-[70px] lg:w-auto lg:min-w-max lg:justify-start lg:gap-3 lg:pr-5 lg:text-left ${isSelected
                    ? 'border-hsecondary bg-hsecondary text-white shadow-sm'
                    : 'border-slate-200 bg-white text-hsecondary hover:border-hsecondary/25 hover:bg-slate-50'
                    }`}
                >
                  <span className={`hidden size-11 shrink-0 items-center justify-center rounded-[14px] lg:flex ${isSelected ? 'bg-white/15' : 'bg-slate-100'}`}>
                    <Icon className="size-5" strokeWidth={1.7} />
                  </span>
                  <div className="flex min-w-0 flex-col items-center lg:items-start">
                    <span className="line-clamp-3 text-[14px] font-semibold leading-[18px] lg:block lg:whitespace-nowrap lg:text-[15px] lg:leading-5">{category.name}</span>
                    <span className={`block whitespace-nowrap text-[12px] leading-4 ${isSelected ? 'text-white/65' : 'text-slate-400'}`}>
                      {formatItemCount(category.productCount ?? 0)}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Product Section */}
      {/* {featuredProduct && (
        <section className="py-10 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Sparkles className="size-5 text-hprimary" />
                <h2 className="text-2xl md:text-3xl text-hsecondary">Featured Product</h2>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row gap-6 bg-linear-to-br from-hprimary/5 to-hsecondary/5 rounded-3xl p-6 md:p-8 border border-hprimary/10 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => router.push(buildPath(`/products/${featuredProduct._id}`))}
            >
              <div className="w-full md:w-56 aspect-square shrink-0 rounded-2xl overflow-hidden bg-white">
                <Image
                  src={featuredProduct.media?.[0]?.file || ''}
                  alt={featuredProduct.name || 'Featured product'}
                  width={224}
                  height={224}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center gap-3">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-hprimary/10 px-3 py-1 text-xs font-semibold text-hprimary uppercase tracking-wide">
                  <Sparkles className="size-3" /> Featured
                </span>
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900">{featuredProduct.name}</h3>
                {featuredProduct.brand && (
                  <p className="text-sm text-gray-500">{featuredProduct.brand}</p>
                )}
                {featuredProduct.summary?.minPrice !== undefined && (
                  <p className="text-lg font-bold text-hsecondary">
                    {featuredProduct.summary.minPrice === featuredProduct.summary.maxPrice
                      ? `$${featuredProduct.summary.minPrice.toFixed(2)}`
                      : `$${featuredProduct.summary.minPrice.toFixed(2)} – $${featuredProduct.summary.maxPrice?.toFixed(2)}`}
                  </p>
                )}
                <button
                  type="button"
                  className="mt-2 w-fit flex items-center gap-2 rounded-full bg-hprimary px-5 py-2.5 text-sm font-semibold text-white hover:bg-hprimary/90 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push(buildPath(`/products/${featuredProduct._id}`));
                  }}
                >
                  Shop Now <ArrowRight className="size-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      )} */}

      <div className="relative pb-16 mt-8">
        <div className="container mx-auto max-w-[1600px] px-4">
          <div className="flex flex-col gap-8 lg:flex-row">
            <aside className="hidden w-72 shrink-0 self-start lg:block">
              {renderFiltersPanel()}
            </aside>

            <main className="flex-1 space-y-6">
              <div className="rounded-[20px] border border-gray-100 bg-white p-3 lg:rounded-3xl lg:p-4">
                <div className="flex items-center justify-between gap-2 lg:gap-6">
                  <div className="hidden lg:block">
                    <h2 className="text-base font-medium text-gray-700">
                      {isLoading
                        ? 'Loading products...'
                        : `${displayedProducts.length} products found`}

                    </h2>
                  </div>
                  <div className="flex min-w-0 flex-1 items-center gap-2 lg:flex-none lg:gap-3">
                    <button
                      type="button"
                      onClick={() => setShowMobileFilters(true)}
                      className="flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-[14px] border border-slate-200 bg-white px-3 text-sm font-medium text-hsecondary transition hover:border-hsecondary/30 hover:bg-slate-50 lg:hidden"
                    >
                      <ListFilter className="size-4" strokeWidth={1.7} />
                      <span>Filters</span>
                      {activeFilterChips.length > 0 && (
                        <span className="flex size-5 items-center justify-center rounded-full bg-hsecondary text-[10px] font-semibold text-white">
                          {activeFilterChips.length}
                        </span>
                      )}
                    </button>

                    <div className="relative min-w-0 flex-1 lg:flex-none">
                      <select
                        value={sortOption}
                        onChange={(event) => {
                          setSortOption(event.target.value as SortOption);
                          setPage(1);
                        }}
                        className="h-11 w-full appearance-none rounded-[14px] border border-gray-200 bg-white py-2.5 pl-3.5 pr-8 text-sm font-medium text-hsecondary shadow-xs transition focus:border-hsecondary/40 focus:outline-hidden focus:ring-1 focus:ring-hsecondary/20 lg:min-w-36 lg:pr-9"
                      >
                        <option value="featured">Featured</option>
                        <option value="price-low-high">Price: low to high</option>
                        <option value="price-high-low">Price: high to low</option>
                        <option value="newest">Newest arrivals</option>
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>
                    <div className="flex shrink-0 items-center rounded-[14px] border border-slate-200 bg-white p-1 shadow-xs" aria-label="Product view">
                      <button
                        type="button"
                        onClick={() => setProductView('grid')}
                        aria-label="Grid view"
                        aria-pressed={productView === 'grid'}
                        className={`flex size-8 items-center justify-center rounded-[10px] transition-colors ${productView === 'grid' ? 'bg-slate-100 text-hsecondary shadow-xs' : 'text-slate-300 hover:text-hsecondary'}`}
                      >
                        <Grid className="size-4" strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setProductView('list')}
                        aria-label="Horizontal card view"
                        aria-pressed={productView === 'list'}
                        className={`flex size-8 items-center justify-center rounded-[10px] transition-colors ${productView === 'list' ? 'bg-slate-100 text-hsecondary shadow-xs' : 'text-slate-300 hover:text-hsecondary'}`}
                      >
                        <List className="size-4" strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                </div>

                {hasActiveFilters && (
                  <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                    {activeFilterChips.map((chip) => (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={chip.onRemove}
                        className="group flex items-center gap-2 rounded-full bg-hprimary-50 px-3 py-1.5 text-sm font-medium text-hprimary-700 transition hover:bg-hprimary-100"
                      >
                        {chip.label}
                        <X className="h-4 w-4 text-hprimary-500 group-hover:text-hprimary-700" />
                      </button>
                    ))}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={handleClearFilters}
                      className="text-sm font-semibold text-hprimary hover:text-hprimary-600 transition-colors"
                    >
                      Reset all
                    </motion.button>
                  </div>
                )}
              </div>

              <div >

                <div ref={productsStartRef} className="mt-6 scroll-mt-24">
                  {isLoading ? (
                    <div className={productView === 'grid'
                      ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4'
                      : 'grid grid-cols-1 gap-4 xl:grid-cols-2'}>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <ProductCardSkeleton key={index} layout={productView === 'grid' ? 'vertical' : 'horizontal'} />
                      ))}
                    </div>
                  ) : displayedProducts.length > 0 ? (
                    <div className={productView === 'grid'
                      ? 'grid min-[300px]:grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4'
                      : 'grid grid-cols-1 gap-4 xl:grid-cols-2'}>
                      {displayedProducts.map((product) => {
                        const defaultVariantId = (() => {
                          if (sortOption === 'price-high-low') {
                            const best = product.variants?.reduce((best, v) =>
                              (v.finalPrice ?? 0) > (best.finalPrice ?? 0) ? v : best,
                              product.variants[0]
                            );
                            return best ? ((best as any)._id || (best as any).id || null) : null;
                          }
                          // When a price filter is active, use the utility to find the best default variant
                          // Priority: single in-range variant > cheapest in-stock in-range > cheapest overall
                          if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
                            const defaultVariant = findDefaultVariantForPriceRange(
                              product,
                              filters.minPrice,
                              filters.maxPrice
                            );
                            return defaultVariant
                              ? ((defaultVariant as any)._id || (defaultVariant as any).id || null)
                              : null;
                          }
                          return undefined;
                        })();

                        return (
                          <div key={product._id} className="h-full">
                            <ProductCard
                              product={product}
                              layout={productView === 'grid' ? 'vertical' : 'horizontal'}
                              defaultVariantId={defaultVariantId}
                              minPrice={filters.minPrice}
                              maxPrice={filters.maxPrice}
                              onClickProduct={(item) => {
                                router.push(buildPath(`/products/${item._id}`));
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyState
                      icon={Package}
                      title="No products found"
                      description="Try adjusting your filters or search terms to uncover more results."
                      actionLabel={hasActiveFilters ? 'Clear filters' : undefined}
                      onAction={hasActiveFilters ? handleClearFilters : undefined}
                    />
                  )}
                </div>

                {pagination.totalPages > 1 && (
                  <div className="mt-12 rounded-3xl border border-slate-100 bg-white px-4 py-4 shadow-[0_8px_30px_rgba(31,41,55,0.04)] sm:px-5">
                    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                      <div className="text-center md:text-left">
                        <p className="text-sm font-medium text-[#7C9085]">
                          Showing{' '}
                          <span className="font-semibold text-hsecondary">
                            {((page - 1) * pagination.limit) + 1}–{Math.min(page * pagination.limit, pagination.total)}
                          </span>{' '}
                          of <span className="font-semibold text-hsecondary">{pagination.total}</span> products
                        </p>
                        <p className="mt-0.5 text-xs text-slate-400">
                          Page {page} of {pagination.totalPages}
                        </p>
                      </div>

                      <nav className="flex max-w-full items-center gap-1.5" aria-label="Product pagination">
                        <button
                          type="button"
                          onClick={() => setPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="flex size-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-hsecondary transition hover:border-hsecondary/30 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="size-4" strokeWidth={1.8} />
                        </button>

                        <div className="flex items-center gap-1">
                          {(() => {
                            const pages: (number | 'ellipsis')[] = [];
                            const totalPages = pagination.totalPages;

                            if (totalPages <= 7) {
                              for (let index = 1; index <= totalPages; index++) pages.push(index);
                            } else {
                              pages.push(1);
                              if (page > 3) pages.push('ellipsis');

                              const start = Math.max(2, page - 1);
                              const end = Math.min(totalPages - 1, page + 1);
                              for (let index = start; index <= end; index++) pages.push(index);

                              if (page < totalPages - 2) pages.push('ellipsis');
                              pages.push(totalPages);
                            }

                            return pages.map((pageNumber, index) => {
                              if (pageNumber === 'ellipsis') {
                                return (
                                  <span
                                    key={`ellipsis-${index}`}
                                    className="hidden size-8 items-center justify-center text-sm tracking-widest text-slate-300 sm:flex"
                                    aria-hidden="true"
                                  >
                                    ···
                                  </span>
                                );
                              }

                              const isActive = pageNumber === page;
                              return (
                                <button
                                  type="button"
                                  key={pageNumber}
                                  onClick={() => setPage(pageNumber)}
                                  aria-label={`Page ${pageNumber}`}
                                  aria-current={isActive ? 'page' : undefined}
                                  className={`${!isActive && pageNumber !== 1 && pageNumber !== totalPages ? 'hidden sm:flex' : 'flex'} size-10 items-center justify-center rounded-full text-sm font-semibold transition ${isActive
                                    ? 'bg-hsecondary text-white shadow-sm'
                                    : 'border border-transparent text-[#7C9085] hover:border-slate-200 hover:bg-slate-50 hover:text-hsecondary'
                                    }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          type="button"
                          onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                          disabled={page === pagination.totalPages}
                          className="flex size-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-hsecondary transition hover:border-hsecondary/30 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                          aria-label="Next page"
                        >
                          <ChevronRight className="size-4" strokeWidth={1.8} />
                        </button>
                      </nav>
                    </div>
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showMobileFilters && (
          <motion.div
            className="fixed inset-0 z-[100] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              type="button"
              className="absolute inset-0 cursor-default bg-slate-950/45 backdrop-blur-[1px]"
              onClick={() => setShowMobileFilters(false)}
              aria-label="Close filters"
            />
            <motion.section
              role="dialog"
              aria-modal="true"
              aria-label="Product filters"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 320 }}
              className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[90dvh] max-w-xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl md:bottom-5 md:rounded-[28px]"
            >
              <div className="relative flex h-10 shrink-0 items-center justify-center">
                <span className="h-1.5 w-10 rounded-full bg-slate-200" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => setShowMobileFilters(false)}
                  className="absolute right-4 top-3 flex size-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-hsecondary"
                  aria-label="Close filters"
                >
                  <X className="size-4" />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {renderFiltersPanel(true)}
              </div>

              <div className="flex shrink-0 items-center gap-3 border-t border-slate-100 bg-white px-5 py-4">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="h-12 rounded-full border border-slate-200 bg-white px-6 text-sm font-medium text-hsecondary transition hover:border-hsecondary/30 hover:bg-slate-50"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setShowMobileFilters(false)}
                  className="h-12 flex-1 rounded-full bg-hsecondary px-6 font-serif text-[17px] font-medium italic text-white shadow-sm transition hover:bg-hsecondary/90"
                >
                  Apply Filters
                </button>
              </div>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
